const STORAGE_KEY = "meta_attribution"

export interface MetaAttribution {
  fbclid?: string
  fbc?: string
  fbp?: string
  campaign?: string
  source_url?: string
}

function getCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : undefined
}

/**
 * Capture Meta attribution data from URL params and cookies.
 * Call this on app boot (main.tsx) after initFacebookPixel().
 *
 * Uses Meta's official Client Parameter Builder library to correctly
 * process fbclid and set _fbc/_fbp cookies without modification.
 */
export function captureMetaAttribution(): void {
  try {
    const params = new URLSearchParams(window.location.search)
    const campaign = params.get("campaign") || undefined

    // Use Meta's Client Param Builder to process fbclid → _fbc cookie correctly
    if (window.clientParamBuilder) {
      window.clientParamBuilder.processAndCollectParams(window.location.href)
    }

    // Read fbc/fbp from the Meta lib (falls back to cookies if lib unavailable)
    const fbc = window.clientParamBuilder?.getFbc() || getCookie("_fbc") || undefined
    const fbp = window.clientParamBuilder?.getFbp() || getCookie("_fbp") || undefined
    const fbclid = params.get("fbclid") || undefined

    // Only save if we have at least one piece of attribution data
    if (!fbclid && !fbc && !fbp && !campaign) return

    const attribution: MetaAttribution = {
      fbclid: fbclid || undefined,
      fbc: fbc || undefined,
      fbp: fbp || undefined,
      campaign: campaign || undefined,
      source_url: window.location.href,
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(attribution))
  } catch (e) {
    console.warn("Failed to capture meta attribution:", e)
  }
}

/**
 * Get fresh fbc value from Meta lib, falling back to localStorage.
 */
export function getFreshFbc(): string | undefined {
  return window.clientParamBuilder?.getFbc() || getCookie("_fbc") || undefined
}

/**
 * Get fresh fbp value from Meta lib, falling back to localStorage.
 */
export function getFreshFbp(): string | undefined {
  return window.clientParamBuilder?.getFbp() || getCookie("_fbp") || undefined
}

/**
 * Get saved Meta attribution data from localStorage.
 */
export function getMetaAttribution(): MetaAttribution | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored ? JSON.parse(stored) : null
  } catch {
    return null
  }
}

/**
 * Generate a unique event ID for Pixel/CAPI deduplication.
 */
export function generateEventId(): string {
  return crypto.randomUUID()
}
